import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { EMAIL_TEMPLATE_VARIABLES } from '@claimup/shared/constants/email-templates';
import {
  listTemplateCatalogue,
  listCompanyTemplates,
  addTemplateFromCatalogue,
  updateCompanyTemplate,
  removeCompanyTemplate,
  updateStandardTemplate,
  seedTemplateCatalogue,
  createStandardTemplate,
  createCompanyTemplate,
  getEmailCadence,
  saveEmailCadence,
  getEmailBrand,
  saveEmailBrand,
  uploadEmailLogo,
  removeEmailLogo,
  previewEmailBrand,
  getEmailWhatsappTarget,
} from '../../../../services/emailDomainService';
import TemplateCard from './TemplateCard';
import CadenceCard from './CadenceCard';
import BrandCard from './BrandCard';
import NuevaPlantilla from './NuevaPlantilla';

/**
 * Plantillas de correo de AR: el catálogo estándar y la escalera de cada cliente.
 *
 * **El orden es de cada cliente**, no del código. Es la lección que ya se
 * aprendió en Kapso: mientras el recorrido vivió en una constante, solo las
 * claves estándar podían ocupar un peldaño y una plantilla escrita para un
 * cliente no entraba nunca en el circuito automático.
 *
 * Por eso la pantalla enseña **una empresa cada vez**: la escalera es de ella, y
 * una vista «todas» invitaría a pensar que el recorrido es común.
 */

const mensajeDeError = err => err.response?.data?.error?.message || 'No se pudo completar la operación';

const ListaDeVariables = () => (
  <div className='bg-white border border-slate-200 rounded-lg p-4'>
    <p className='text-sm font-medium text-slate-800 mb-1'>Variables disponibles</p>
    <p className='text-xs text-slate-500 mb-3'>
      Se escriben entre llaves dobles. Es una lista cerrada: al guardar se rechaza cualquier otra, para que ningún
      correo salga con un hueco.
    </p>
    <dl className='space-y-1.5'>
      {Object.entries(EMAIL_TEMPLATE_VARIABLES).map(([nombre, que]) => (
        <div key={nombre} className='flex items-start gap-2 text-xs'>
          <dt className='font-mono text-slate-700 shrink-0'>{`{{${nombre}}}`}</dt>
          <dd className='text-slate-500'>{que}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/**
 * `esCliente`: la misma pantalla, vista por el dueño de la empresa.
 *
 * Cambia tres cosas, y ninguna es cosmética:
 *   - **sin selector de empresa**: solo hay una, la suya;
 *   - **sin el bloque del catálogo estándar**, que es de ClaimUp y el backend
 *     no le deja editar — enseñárselo sería ofrecerle un 403;
 *   - una plantilla nueva es **suya**, no del catálogo.
 */
/**
 * `soloTextos`: solo lo que dice cada mensaje (asunto, cuerpo, botones, marca).
 * El cuándo —cadencia y orden de la escalera— vive en «Configuración»
 * (`EmailSchedule`). Es lo que ve la empresa en Plantillas de correo.
 */
const EmailTemplatesTab = ({ empresas, esCliente = false, soloTextos = false }) => {
  const [empresa, setEmpresa] = useState('');
  const [catalogo, setCatalogo] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [cadencia, setCadencia] = useState(null);
  const [marca, setMarca] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [verCatalogo, setVerCatalogo] = useState(false);
  const [nueva, setNueva] = useState(false);
  const [propia, setPropia] = useState(false);

  // Sin empresa elegida no hay escalera que enseñar, así que se entra por la
  // primera en vez de por una pantalla vacía que obliga a un clic de peaje.
  useEffect(() => {
    if (!empresa && empresas.length > 0) setEmpresa(empresas[0][0]);
  }, [empresa, empresas]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [cat, suyas, cad, mar, wa] = await Promise.all([
        listTemplateCatalogue(),
        empresa ? listCompanyTemplates(empresa) : Promise.resolve([]),
        empresa ? getEmailCadence(empresa) : Promise.resolve(null),
        empresa ? getEmailBrand(empresa) : Promise.resolve(null),
        // Una sola vez por empresa, no una por tarjeta: todas ofrecen el
        // mismo botón de WhatsApp porque es el mismo número.
        empresa ? getEmailWhatsappTarget(empresa) : Promise.resolve(null),
      ]);
      setCatalogo(cat);
      setPlantillas(suyas);
      setCadencia(cad);
      setMarca(mar);
      setWhatsapp(wa);
    } catch (err) {
      console.error('Error cargando las plantillas:', err);
      toast.error(mensajeDeError(err));
    } finally {
      setCargando(false);
    }
  }, [empresa]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const nombreEmpresa = useMemo(() => {
    const fila = empresas.find(([id]) => id === empresa);
    return fila ? fila[1] : '';
  }, [empresa, empresas]);

  /**
   * La previa, memorizada.
   *
   * Tiene que ser estable: `BrandCard` la pide dentro de un `useEffect` que la
   * lleva en las dependencias, asi que una funcion nueva en cada render
   * dispararia una llamada en cada render, en bucle.
   */
  const previsualizar = useCallback(datos => previewEmailBrand(empresa, datos), [empresa]);

  /** Envuelve una acción: avisa del fallo y recarga, que es lo que hacen todas. */
  const accion = async (fn, exito) => {
    try {
      await fn();
      if (exito) toast.success(exito);
      await cargar();
    } catch (err) {
      toast.error(mensajeDeError(err));
    }
  };

  const sinAñadir = useMemo(() => {
    const suyas = new Set(plantillas.map(p => p.key));
    return catalogo.filter(c => !suyas.has(c.key));
  }, [catalogo, plantillas]);

  const enEscalera = plantillas.filter(p => p.escalation_order !== null);
  const fuera = plantillas.filter(p => p.escalation_order === null);

  if (empresas.length === 0) {
    return (
      <div className='text-center py-12 bg-white border border-slate-200 rounded-lg'>
        <p className='text-sm text-slate-500'>Ninguna empresa ha conectado su correo todavía.</p>
        <p className='text-xs text-slate-400 mt-1'>
          Las plantillas se configuran por empresa, así que primero hace falta una.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-end gap-4'>
        {!esCliente && (
          <div>
            <label htmlFor='plantillas-empresa' className='block text-xs font-medium text-slate-600 mb-1'>
              Empresa
            </label>
            <select
              id='plantillas-empresa'
              value={empresa}
              onChange={e => setEmpresa(e.target.value)}
              className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
            >
              {empresas.map(([id, nombre]) => (
                <option key={id} value={id}>
                  {nombre}
                </option>
              ))}
            </select>
          </div>
        )}
        <p className='text-xs text-slate-500 max-w-md'>
          {soloTextos
            ? 'Lo que dice cada correo. Cuándo sale y en qué orden se decide en Configuración.'
            : esCliente
            ? 'Así contactas por correo con los deudores que no te responden: qué se les escribe, en qué orden y qué días.'
            : 'El recorrido es de esta empresa. Otra puede tener las mismas plantillas en otro orden, o no tener alguna.'}
        </p>
      </div>

      {cargando ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin rounded-lg h-8 w-8 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
          <div className='lg:col-span-2 space-y-4'>
            {marca && (
              <BrandCard
                // Mismo motivo que la cadencia: remonta al cambiar de empresa
                // y despues de guardar, para que el borrador no se quede con la
                // marca de la empresa anterior.
                key={`marca-${empresa}-${marca.updatedAt || 'nueva'}`}
                marca={marca}
                nombreEmpresa={nombreEmpresa}
                onGuardar={patch => accion(() => saveEmailBrand(empresa, patch), 'Marca guardada')}
                onSubirLogo={file => accion(() => uploadEmailLogo(empresa, file), 'Logo subido')}
                onQuitarLogo={() => accion(() => removeEmailLogo(empresa), 'Logo quitado')}
                onPrevisualizar={previsualizar}
              />
            )}

            {cadencia && !soloTextos && (
              <CadenceCard
                // Remonta al cambiar de empresa y después de guardar. Sin esto
                // el borrador interno se quedaría con la cadencia de la empresa
                // anterior, que es de las cosas que no dan error y aun así te
                // hacen guardar los días de otro cliente.
                key={`${empresa}-${cadencia.updatedAt || 'nueva'}`}
                cadencia={cadencia}
                onGuardar={patch => accion(() => saveEmailCadence(empresa, patch), 'Cadencia guardada')}
              />
            )}

            <div>
              <h2 className='text-sm font-medium text-slate-800'>La escalera</h2>
              <p className='text-xs text-slate-500 mb-2'>
                El orden en que se contacta en frío. Se recorre de arriba abajo, y para cuando se acaba: no se repite el
                último.
              </p>

              {enEscalera.length === 0 ? (
                <div className='bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500'>
                  Esta empresa no tiene escalera. Sin al menos un peldaño, el envío automático no manda nada.
                </div>
              ) : (
                <div className='space-y-3'>
                  {enEscalera.map(p => (
                    <TemplateCard
                whatsapp={whatsapp}
                      color={marca?.primary_color}
                      key={p.key}
                      plantilla={p}
                      onGuardar={patch => accion(() => updateCompanyTemplate(empresa, p.key, patch), 'Guardado')}
                      onCambiarPeldano={
                        soloTextos
                          ? undefined
                          : orden =>
                              accion(
                                () => updateCompanyTemplate(empresa, p.key, { escalation_order: orden }),
                                orden === null
                                  ? 'Fuera de la escalera: ahora solo se manda a mano'
                                  : `Pasa a ser el contacto nº ${orden}`
                              )
                      }
                      onCambiarRepeticiones={soloTextos ? undefined : veces =>
                        accion(
                          () => updateCompanyTemplate(empresa, p.key, { repeats: veces }),
                          veces === 1 ? 'Se manda una vez' : `Se manda ${veces} veces`
                        )
                      }
                      onQuitar={() => accion(() => removeCompanyTemplate(empresa, p.key), 'Plantilla quitada')}
                    />
                  ))}
                </div>
              )}
            </div>

            {fuera.length > 0 && (
              <div>
                <h2 className='text-sm font-medium text-slate-800'>Fuera de la escalera</h2>
                <p className='text-xs text-slate-500 mb-2'>
                  Existen para esta empresa pero no entran en el circuito automático: solo se mandan a mano.
                </p>
                <div className='space-y-3'>
                  {fuera.map(p => (
                    <TemplateCard
                whatsapp={whatsapp}
                      color={marca?.primary_color}
                      key={p.key}
                      plantilla={p}
                      onGuardar={patch => accion(() => updateCompanyTemplate(empresa, p.key, patch), 'Guardado')}
                      onCambiarPeldano={
                        soloTextos
                          ? undefined
                          : orden =>
                              accion(
                                () => updateCompanyTemplate(empresa, p.key, { escalation_order: orden }),
                                `Pasa a ser el contacto nº ${orden}`
                              )
                      }
                      onQuitar={() => accion(() => removeCompanyTemplate(empresa, p.key), 'Plantilla quitada')}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className='space-y-4'>
            <div className='bg-white border border-slate-200 rounded-lg p-4'>
              <p className='text-sm font-medium text-slate-800 mb-1'>Añadir del catálogo</p>
              {catalogo.length === 0 ? (
                /* Sin catálogo no hay nada que añadir, y decir «ya las tiene
                   todas» sería afirmar lo contrario de lo que pasa: eso es lo
                   que ponía antes y despistaba a quien entraba por primera vez. */
                <>
                  <p className='text-xs text-slate-500'>
                    El catálogo está vacío. Crea las tres de arranque y luego edítalas, o escribe una desde cero.
                  </p>
                  <button
                    type='button'
                    onClick={() => accion(seedTemplateCatalogue, 'Catálogo creado: ya puedes editarlas')}
                    className='mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700'
                  >
                    <SparklesIcon className='w-3.5 h-3.5' />
                    Crear las de arranque
                  </button>
                </>
              ) : sinAñadir.length === 0 ? (
                <p className='text-xs text-slate-500'>Esta empresa ya tiene todas las del catálogo.</p>
              ) : (
                <ul className='space-y-2 mt-2'>
                  {sinAñadir.map(c => (
                    <li key={c.key} className='flex items-center justify-between gap-2'>
                      <span className='text-xs text-slate-600 min-w-0 truncate'>{c.description || c.key}</span>
                      <button
                        type='button'
                        onClick={() => accion(() => addTemplateFromCatalogue(empresa, c.key), 'Añadida a esta empresa')}
                        className='shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800'
                      >
                        <PlusIcon className='w-3.5 h-3.5' />
                        Añadir
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Escribir una propia. Va aquí, al lado de «Añadir del catálogo»,
                porque son las dos formas de que una plantilla acabe siendo de
                esta empresa: copiar la de ClaimUp o escribirla. */}
            <div className='bg-white border border-slate-200 rounded-lg p-4'>
              {propia ? (
                <NuevaPlantilla
              whatsapp={whatsapp}
                  color={marca?.primary_color}
                  empresa={empresa}
                  onCrear={async datos => {
                    await accion(() => createCompanyTemplate(empresa, datos), 'Plantilla creada');
                    setPropia(false);
                  }}
                  onCancelar={() => setPropia(false)}
                />
              ) : (
                <>
                  <p className='text-sm font-medium text-slate-800 mb-1'>
                    {esCliente ? 'Escribir la tuya' : 'Escribir una para esta empresa'}
                  </p>
                  <p className='text-xs text-slate-500 mb-2'>
                    Con tus palabras. Entra fuera de la escalera: luego le das su sitio.
                  </p>
                  <button
                    type='button'
                    onClick={() => setPropia(true)}
                    className='inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800'
                  >
                    <PlusIcon className='w-3.5 h-3.5' />
                    Escribir una plantilla
                  </button>
                </>
              )}
            </div>

            <ListaDeVariables />

            {!esCliente && (
              <div className='bg-white border border-slate-200 rounded-lg p-4'>
                <button
                  type='button'
                  onClick={() => setVerCatalogo(v => !v)}
                  className='text-sm font-medium text-slate-800 hover:text-blue-700'
                >
                  {verCatalogo ? 'Ocultar' : 'Ver'} el catálogo estándar ({catalogo.length})
                </button>
                <p className='text-xs text-slate-500 mt-1'>
                  Los textos maestros de ClaimUp, de los que se parte al dar de alta un cliente. Editarlos{' '}
                  <strong>no cambia</strong> las copias ya repartidas.
                </p>

                {nueva ? (
                  <div className='mt-3'>
                    <NuevaPlantilla
              whatsapp={whatsapp}
                      empresa={empresa}
                      onCrear={async datos => {
                        await accion(() => createStandardTemplate(datos), 'Plantilla creada');
                        setNueva(false);
                      }}
                      onCancelar={() => setNueva(false)}
                    />
                  </div>
                ) : (
                  <button
                    type='button'
                    onClick={() => setNueva(true)}
                    className='mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800'
                  >
                    <PlusIcon className='w-3.5 h-3.5' />
                    Escribir una plantilla nueva
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {verCatalogo && !cargando && !esCliente && (
        <div className='space-y-3'>
          <h2 className='text-sm font-medium text-slate-800'>Catálogo estándar</h2>
          {catalogo.map(c => (
            <TemplateCard
                whatsapp={whatsapp}
              key={c.key}
              plantilla={c}
              onGuardar={patch => accion(() => updateStandardTemplate(c.key, patch), 'Catálogo actualizado')}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesTab;
